import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { usd } from "@/lib/addons";
import { STEPS, EMAIL_RE } from "@/lib/booking";
import { Stepper, Field } from "./Controls";

export const BookingIntro = () => (
  <div className="max-w-3xl">
    <p className="eyebrow mb-3 sm:mb-4">Book your assessment</p>
    <h1 className="h-editorial !text-[1.75rem] sm:!text-4xl lg:!text-5xl" data-testid="booking-heading">Reserve your Home Hyper Assessment.</h1>
    <p className="mt-3 sm:mt-4 max-w-xl text-[15px] sm:text-lg leading-relaxed text-sec">About two minutes. $250 deposit today; the balance is due 24 hours before your confirmed visit.</p>
  </div>
);

export const StepHeader = ({ step }) => (
  <>
    <Stepper step={step} />
    <h2 id="step-title" className="h-sub mt-5 sm:mt-7" data-testid="step-title">{STEPS[step]}</h2>
  </>
);

export const StepFooter = ({ step, onBack, onNext, disabled, label, busy, error, secure }) => (
  <div className="mt-8 sm:mt-9 rule pt-5 sm:pt-6">
    {error && <p className="error-text mb-4" role="alert" data-testid="booking-error">{error}</p>}
    <div className="flex flex-col sm:flex-row-reverse sm:items-center sm:justify-between gap-3 sm:gap-4">
      <button type="button" onClick={onNext} disabled={disabled || busy} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-forest" data-testid="booking-continue">
        {busy ? <Loader2 size={18} className="animate-spin" /> : null}{label} {!busy && <ArrowRight size={18} />}
      </button>
      {step > 0 ? (
        <button type="button" onClick={onBack} className="btn-secondary border-transparent sm:border-sec/60" data-testid="booking-back" disabled={busy}><ArrowLeft size={16} /> Back</button>
      ) : (
        <Link to="/" className="link-arrow justify-center sm:justify-start" data-testid="booking-exit"><ArrowLeft size={16} /> Back to the site</Link>
      )}
    </div>
    {secure && (
      <p className="mt-3 flex items-center justify-center sm:justify-end gap-1.5 text-xs text-sec" data-testid="secure-note"><Lock size={12} /> Secure checkout • Deposit applied to your total</p>
    )}
  </div>
);

export const Confirmation = ({ r }) => (
  <div className="mt-6 reveal" data-testid="booking-done">
    <p className="eyebrow mb-4">Request received</p>
    <h2 className="h-editorial">Thank you. We’ll confirm your appointment within 24 hours.</h2>
    <dl className="mt-7 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm rounded-md border border-divider p-5">
      <dt className="mono text-sec pt-0.5">Reference</dt><dd className="font-mono text-base text-ink" data-testid="booking-reference">{r.reference}</dd>
      <dt className="mono text-sec pt-0.5">Status</dt><dd className="text-ink" data-testid="booking-status">Requested · awaiting confirmation</dd>
      <dt className="mono text-sec pt-0.5">Visit</dt><dd className="text-ink">{r.date_label} · {r.window_label}</dd>
      <dt className="mono text-sec pt-0.5">Due today</dt><dd className="text-ink">{usd(r.due_today_cents / 100)} · remaining {usd(r.remaining_balance_cents / 100)} due 24 hours before your visit</dd>
    </dl>
    <p className="mt-5 text-sm text-ink leading-relaxed" data-testid="booking-email-note">
      Online payment isn’t switched on yet, so nothing has been charged. Your request is saved under the reference above and we’ll send a secure payment link with your confirmation.
    </p>
    <div className="mt-8 flex flex-wrap gap-4">
      <Link to="/" className="btn-secondary" data-testid="done-home-link"><ArrowLeft size={16} /> Back to the site</Link>
    </div>
  </div>
);

export const WaitlistForm = ({ zip, onSubmit, busy, error, done }) => {
  const [v, setV] = useState({ name: "", email: "" });
  const [t, setT] = useState(false);
  const errs = { name: v.name.trim().length < 2 ? "Enter your name." : "", email: EMAIL_RE.test(v.email.trim()) ? "" : "Enter a valid email address." };
  const ok = !errs.name && !errs.email;
  if (done) {
    return (
      <div className="mt-8 reveal" data-testid="waitlist-done">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-mint text-ink"><Check size={18} /></span>
        <h2 className="h-sub mt-4">We’ll let you know when we reach your area.</h2>
        <p className="mt-2 text-sm text-ink">You’re on the list for ZIP {zip}.</p>
        <Link to="/" className="link-arrow mt-6" data-testid="done-home-link"><ArrowLeft size={16} /> Back to the site</Link>
      </div>
    );
  }
  return (
    <div className="mt-8 rule pt-7" data-testid="waitlist-form">
      <h2 className="h-sub">Join the waitlist for ZIP {zip}</h2>
      <p className="mt-2 text-sm text-ink">We’re starting on the Upper East Side and expanding from there. Leave your details and we’ll email you when your area opens.</p>
      <div className="mt-5 grid gap-5">
        <Field id="wl-name" label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={t ? errs.name : ""} data-testid="waitlist-name" autoComplete="name" />
        <Field id="wl-email" label="Email" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} error={t ? errs.email : ""} data-testid="waitlist-email" autoComplete="email" />
      </div>
      {error && <p className="error-text mt-4" role="alert">{error}</p>}
      <button type="button" disabled={busy} onClick={() => { setT(true); if (ok) onSubmit({ name: v.name.trim(), email: v.email.trim() }); }} className="btn-primary mt-6" data-testid="waitlist-submit">
        {busy ? <Loader2 size={18} className="animate-spin" /> : null} Join the waitlist
      </button>
    </div>
  );
};
